import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { insertAtBeginningOfPage } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { markFilesIndexStartedInCache } from '@/hooks/files/cache/fileCacheSync'
import {
  organizationFilesIndexStatusQueryKeys,
  organizationFilesQueryKeys,
} from '@/hooks/organization/types'
import { boostOrganizationFilesIndexPolling } from '@/hooks/organization/useOrganizationFilesIndexStatus'

import type { PaginatedResponse } from '@/types/Api'
import type { File as UploadedFile } from '@/types/File'
import type { UUID } from '@/types/Id'

export const BATCH_SIZE = 5

interface UploadOrganizationFilesArgs {
  orgId: UUID | ''
  batchSize?: number
  onProgress?: (progress: number) => void
}

interface UploadOrganizationFilesMutationArgs {
  files: File[]
  tag_ids: UUID[]
  extract_entities: boolean
  unrestricted: boolean
}

const uploadFiles = async (
  orgId: UUID | '',
  files: Array<File>,
  accessToken: string | undefined | null,
  tag_ids: UUID[],
  extract_entities: boolean,
  batchSize: number,
  onProgress?: (progress: number) => void,
  unrestricted?: boolean,
): Promise<UploadedFile[]> => {
  if (!orgId) {
    throw new Error('No organization ID provided.')
  }
  if (accessToken === null || accessToken === undefined) {
    throw new Error('No access token provided.')
  }

  const allUploadedFiles: UploadedFile[] = []

  // Process files in batches of 5
  for (let i = 0; i < files.length; i += batchSize) {
    const batch = files.slice(i, i + batchSize)

    let url = new URL(`${API_URL}/api/v1/organizations/${orgId}/files`)
    let searchParams = new URLSearchParams()
    if (tag_ids.length > 0) {
      searchParams.set('tag_ids', tag_ids.map((id) => id.toString()).join(','))
    }
    if (extract_entities) {
      searchParams.set('extract_entities', 'true')
    }
    if (unrestricted !== undefined) {
      searchParams.set('unrestricted', unrestricted ? 'true' : 'false')
    }
    url.search = searchParams.toString()

    const formData = new FormData()
    for (const file of batch) {
      formData.append('files', file)
    }

    const res = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    if (!res.ok) {
      throw new SweetspotError({
        name: 'UPLOAD_FILE_ERROR',
        message: res.statusText,
        cause: {
          status: res.status,
        },
      })
    }

    if (onProgress) {
      onProgress(i / files.length)
    }

    const fileList = (await res.json()) as PaginatedResponse<UploadedFile>
    allUploadedFiles.push(...fileList.data)
  }

  return allUploadedFiles
}

export const useUploadOrganizationFiles = ({
  orgId,
  batchSize = BATCH_SIZE,
  onProgress,
}: UploadOrganizationFilesArgs) => {
  const user = useAuthInfo()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation<
    UploadedFile[],
    Error,
    UploadOrganizationFilesMutationArgs,
    PaginatedResponse<UploadedFile>
  >({
    mutationFn: ({ tag_ids, files, extract_entities, unrestricted }) =>
      uploadFiles(
        orgId,
        files,
        user?.accessToken,
        tag_ids,
        extract_entities,
        batchSize,
        onProgress,
        unrestricted,
      ),
    onError: (err, req, context) => {
      // rollback to the previous value
      const key = organizationFilesQueryKeys.organization({ orgId })
      queryClient.setQueryData(key, context)
      handleError(err)
    },
    onSuccess: (data, req) => {
      const key = organizationFilesQueryKeys.organization({ orgId })
      queryClient.setQueryData<PaginatedResponse<UploadedFile>>(
        key,
        (oldData) => {
          const result = insertAtBeginningOfPage(
            { data, count: data.length, has_more: false },
            oldData,
          )
          return result
        },
      )

      boostOrganizationFilesIndexPolling({
        queryClient,
        orgId,
      })
      markFilesIndexStartedInCache({
        queryClient,
        fileIds: data.map((file) => file.id),
        orgId,
      })
      void queryClient.invalidateQueries({
        queryKey: organizationFilesIndexStatusQueryKeys.all(),
      })
    },
  })
}
